import React, { useState, useEffect } from 'react';
import {
    ConnectPersonalQuickReactionOverlayWS,
    DisconnectPersonalQuickReactionOverlayWS
} from '../../utilities/WS/PersonalQuickReactionOverlayWS';
import { GetConfig, GetWinner } from '../../utilities/DL/PersonalQuickReaction';
import { PersonalQuickReactionOverlay as PersonalQuickReactionOverlayView } from './Overlays/PersonalQuickReactionOverlay';

/**
 * COMPONENT: PersonalQuickReactionOverlay (Parent Container / OBS Controller)
 * -----------------------------------------------------------------------------
 * Controller component for the Live Broadcast Overlay (`/abcdef/overlays/quick-reaction`).
 * Responsibilities:
 *  1. Enforces transparent DOM background & zero-scroll environment for OBS/vMix.
 *  2. Loads initial room configuration (`GetConfig`) and winner record (`GetWinner`).
 *  3. Establishes persistent real-time connection (`ConnectPersonalQuickReactionOverlayWS`).
 *  4. Manages lifecycle transitions (`flowState`: 'idle' | 'entering' | 'visible' | 'fadingOut').
 *  5. Relays synchronized state to the visual presentation component.
 * -----------------------------------------------------------------------------
 * 
 * @returns {JSX.Element} The rendered overlay container and connection indicators.
 */
export function PersonalQuickReactionOverlay() {
    // --- DISPLAY & CONFIGURATION STATES ---
    const [offsetY, setOffsetY] = useState(0);
    const [mode, setMode] = useState('invitation'); // 'invitation' | 'winner'
    const [qrCount, setQrCount] = useState(1); // 1 | 2
    const [banner1536Url, setBanner1536Url] = useState('');
    const [banner1920Url, setBanner1920Url] = useState('');

    // --- WINNER RECORD STATES ---
    const [winnerName, setWinnerName] = useState('');
    const [reactionTimeMs, setReactionTimeMs] = useState(0);
    const [badgeName, setBadgeName] = useState('');
    const [showConfetti, setShowConfetti] = useState(false);

    // --- CONNECTION & LIFECYCLE FLOW STATES ---
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [flowState, setFlowState] = useState('idle'); // 'idle' | 'entering' | 'visible' | 'fadingOut'

    // -------------------------------------------------------------------------
    // 1. Force Transparent Background for OBS Studio & vMix Browser Sources
    // -------------------------------------------------------------------------
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
            rootElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;

            if (rootElement) {
                rootElement.style.removeProperty('background-color');
                rootElement.style.removeProperty('overflow');
            }
        };
    }, []);

    // -------------------------------------------------------------------------
    // 2. Control Smooth CSS Animation Transitions (500ms duration)
    // -------------------------------------------------------------------------
    useEffect(() => {
        let timerIdentifier;

        if (flowState === 'entering') {
            timerIdentifier = setTimeout(() => {
                setFlowState('visible');
            }, 500);
        } else if (flowState === 'fadingOut') {
            timerIdentifier = setTimeout(() => {
                setFlowState('idle');
                setShowConfetti(false);
            }, 500);
        }

        return () => clearTimeout(timerIdentifier);
    }, [flowState]);

    const handleOverlayVisibilityChange = (visible) => {
        if (visible) {
            setFlowState((previousState) => {
                if (previousState === 'idle' || previousState === 'fadingOut') {
                    return 'entering';
                }
                return previousState;
            });
        } else {
            setFlowState((previousState) => {
                if (previousState !== 'idle' && previousState !== 'fadingOut') {
                    return 'fadingOut';
                }
                return previousState;
            });
        }
    };

    // -------------------------------------------------------------------------
    // 3. Initial Snapshot Loading and Real-Time WebSocket Connection
    // -------------------------------------------------------------------------
    useEffect(() => {
        const handleIncomingWebSocketMessage = (messagePayload) => {
            if (!messagePayload || !messagePayload.type) return;

            const messageType = String(messagePayload.type).toLowerCase();
            const messageData = messagePayload.data || messagePayload;

            // EVENT: personalQuickReaction.overlayUpdated, state, config
            if (
                messageType === 'personalquickreaction.overlayupdated' ||
                messageType === 'overlayupdated' ||
                messageType === 'overlay_updated' ||
                messageType === 'state' ||
                messageType === 'config'
            ) {
                const rawVisible =
                    messageData.isVisible ??
                    messageData.isOverlayVisible ??
                    messageData.overlayVisible ??
                    messagePayload.isVisible ??
                    messagePayload.isOverlayVisible ??
                    messagePayload.overlayVisible;

                if (rawVisible !== undefined) {
                    handleOverlayVisibilityChange(Boolean(rawVisible));
                }

                const rawMode =
                    messageData.mode ||
                    messageData.overlayMode ||
                    messageData.bannerMode ||
                    messagePayload.mode ||
                    messagePayload.overlayMode ||
                    messagePayload.bannerMode;

                if (rawMode) {
                    setMode(rawMode);
                    setShowConfetti(rawMode === 'winner');
                }

                const rawQr =
                    messageData.qrCount ??
                    messageData.overlayQrCount ??
                    messagePayload.qrCount ??
                    messagePayload.overlayQrCount;

                if (rawQr !== undefined) {
                    setQrCount(Number(rawQr) || 1);
                }

                const rawOffset =
                    messageData.offsetY ??
                    messageData.overlayOffsetY ??
                    messagePayload.offsetY ??
                    messagePayload.overlayOffsetY;

                if (rawOffset !== undefined) {
                    setOffsetY(Number(rawOffset) || 0);
                }

                const rawMd =
                    messageData.banner1536Url ??
                    messageData.overlayBanner1536Url ??
                    messageData.bannerImageMdUrl ??
                    messagePayload.banner1536Url ??
                    messagePayload.overlayBanner1536Url ??
                    messagePayload.bannerImageMdUrl;

                if (rawMd !== undefined) {
                    setBanner1536Url(rawMd || '');
                }

                const rawLg =
                    messageData.banner1920Url ??
                    messageData.overlayBanner1920Url ??
                    messageData.bannerImageLgUrl ??
                    messagePayload.banner1920Url ??
                    messagePayload.overlayBanner1920Url ??
                    messagePayload.bannerImageLgUrl;

                if (rawLg !== undefined) {
                    setBanner1920Url(rawLg || '');
                }
            }
            // EVENT: personalQuickReaction.newPlay (Update winner celebration in real-time)
            else if (
                messageType === 'personalquickreaction.newplay' ||
                messageType === 'newplay' ||
                messageType === 'new_play'
            ) {
                if (messageData.isWinner || messageData.rankPosition === 1) {
                    if (messageData.participantName) {
                        setWinnerName(messageData.participantName);
                    }
                    if (messageData.reactionTimeMs !== undefined) {
                        setReactionTimeMs(Number(messageData.reactionTimeMs) || 0);
                    }
                    if (messageData.badgeName) {
                        setBadgeName(messageData.badgeName);
                    }
                    setShowConfetti(true);
                }
            }
            // EVENT: personalQuickReaction.reset (Room reset / user purge)
            else if (
                messageType === 'personalquickreaction.reset' ||
                messageType === 'reset'
            ) {
                setWinnerName('');
                setReactionTimeMs(0);
                setBadgeName('');
                setShowConfetti(false);
            }
        };

        const initializeOverlay = async () => {
            // 1. Retrieve initial game and overlay configuration from REST API
            try {
                const configResponse = await GetConfig();
                if (configResponse && configResponse.success && configResponse.data) {
                    const cfg = configResponse.data;

                    if (cfg.overlayOffsetY !== undefined) setOffsetY(cfg.overlayOffsetY);
                    if (cfg.overlayMode) setMode(cfg.overlayMode);
                    if (cfg.overlayQrCount !== undefined) setQrCount(cfg.overlayQrCount);
                    if (cfg.overlayBanner1536Url) setBanner1536Url(cfg.overlayBanner1536Url);
                    if (cfg.overlayBanner1920Url) setBanner1920Url(cfg.overlayBanner1920Url);

                    const isVisible = Boolean(cfg.overlayVisible || cfg.isOverlayVisible);
                    if (isVisible) {
                        setFlowState('visible');
                        if (cfg.overlayMode === 'winner') {
                            setShowConfetti(true);
                        }
                    } else {
                        setFlowState('idle');
                    }
                }
            } catch (err) {
                console.warn('[PersonalQuickReactionOverlay] Error al cargar configuración inicial:', err);
            }

            // 2. Retrieve current winner record from REST API
            try {
                const winnerResponse = await GetWinner();
                if (winnerResponse && winnerResponse.success && winnerResponse.data) {
                    const win = winnerResponse.data;
                    if (win.participantName) setWinnerName(win.participantName);
                    if (win.reactionTimeMs !== undefined) setReactionTimeMs(win.reactionTimeMs);
                    if (win.badgeName) setBadgeName(win.badgeName);
                }
            } catch (err) {
                console.warn('[PersonalQuickReactionOverlay] Error al cargar récord de ganador:', err);
            }

            // 3. Connect to WebSocket channel
            ConnectPersonalQuickReactionOverlayWS(
                handleIncomingWebSocketMessage,
                (status) => setConnectionStatus(status)
            );
        };

        initializeOverlay();

        return () => {
            DisconnectPersonalQuickReactionOverlayWS();
        };
    }, []);

    // -------------------------------------------------------------------------
    // 4. Render Overlay Visual Presentation
    // -------------------------------------------------------------------------
    return (
        <>
            <PersonalQuickReactionOverlayView
                flowState={flowState}
                offsetY={offsetY}
                mode={mode}
                qrCount={qrCount}
                banner1536Url={banner1536Url}
                banner1920Url={banner1920Url}
                winnerName={winnerName}
                reactionTimeMs={reactionTimeMs}
                badgeName={badgeName}
                showConfetti={showConfetti}
            />

            {/* Subtle error indicator for broadcast operators */}
            {connectionStatus === 'error' && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 5,
                        right: 5,
                        backgroundColor: '#ef4444',
                        color: '#ffffff',
                        padding: '3px 8px',
                        fontSize: '9px',
                        borderRadius: '4px',
                        opacity: 0.8,
                        zIndex: 99999,
                        fontFamily: 'sans-serif'
                    }}
                >
                    PQR WS Desconectado
                </div>
            )}
        </>
    );
}

export default PersonalQuickReactionOverlay;
